/**
 * A date and time field whose language is ours.
 *
 * ## Why this exists
 *
 * `<input type="datetime-local">` renders its own chrome -- the calendar, the
 * month names, the `dd.mm.åååå` format hint, the calendar-icon tooltip -- in
 * the **browser's** UI language, and Chrome and Safari deliberately ignore the
 * document's `lang`. ABG's browser is set to `nb`, so a captain app that is
 * English everywhere else was handing them a Norwegian picker, with no
 * page-level override available.
 *
 * ## Why it is desktop-only
 *
 * On a phone the native control is a full-screen OS picker, which is the right
 * thing to hand somebody on a wet deck: big targets, one thumb, and it follows
 * the language their phone is already in -- which is more use to a Greek master
 * than our English would be. So the native input is kept wherever there is no
 * fine pointer, and this replaces it wherever there is one.
 *
 * `(any-pointer: fine)` rather than a width query or a user-agent sniff: the
 * question is "is there a mouse or trackpad", not "is the window narrow". A
 * captain with a 900 px browser window on a laptop still wants the desktop
 * control; an iPad without a trackpad does not. A hybrid laptop reports a fine
 * pointer and gets this control, which is correct -- it is usable by touch too,
 * it is simply not a full-screen wheel.
 *
 * ## The value contract is unchanged
 *
 * In and out is `YYYY-MM-DDTHH:mm` in the device's own zone -- exactly what
 * `datetime-local` produces and what `@abg/ui/time`'s `toLocalInputValue` and
 * `fromLocalInputValue` already speak. Nothing downstream knows which control
 * a captain saw.
 *
 * ## Typing is a first-class path
 *
 * Somebody with a window in front of them will type it, so the text field is
 * the control and the calendar is the affordance beside it. Three forms are
 * accepted and the rest are refused by name rather than guessed at, which is
 * the same stance the coordinate parser takes (decision 26): lenient about
 * format, strict about meaning. `03/09/2026` is **not** accepted -- it means
 * two different days either side of the Atlantic, and this app files
 * applications with an authority.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  ACCEPTED_FORMATS,
  MONTHS_FULL,
  WEEKDAYS,
  formatDisplay,
  pad,
  parseTyped,
  splitValue,
  toValue,
} from '@abg/ui/datetime'

/** Native wherever there is no mouse. Read once -- a pointer does not appear
 *  mid-session, and re-reading on every render would make the control flicker
 *  between implementations. */
function hasFinePointer(): boolean {
  if (typeof window === 'undefined' || !window.matchMedia) return true
  return window.matchMedia('(any-pointer: fine)').matches
}

export function DateTimeField({
  value,
  onChange,
  title,
  ariaLabel,
  required,
  hintId,
}: {
  /** `YYYY-MM-DDTHH:mm`, local. */
  value: string
  onChange: (value: string) => void
  title?: string
  ariaLabel?: string
  required?: boolean
  hintId?: string
}) {
  const [fine] = useState(hasFinePointer)

  if (!fine) {
    return (
      <input
        type="datetime-local"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        title={title}
        aria-label={ariaLabel}
        aria-describedby={hintId}
        required={required}
      />
    )
  }

  return (
    <DesktopPicker
      value={value}
      onChange={onChange}
      title={title}
      ariaLabel={ariaLabel}
      required={required}
      hintId={hintId}
    />
  )
}

/** The only minutes the dropdown offers. See the note where they are rendered. */
const QUARTERS = [0, 15, 30, 45]

function DesktopPicker({
  value,
  onChange,
  title,
  ariaLabel,
  required,
  hintId,
}: {
  value: string
  onChange: (value: string) => void
  title?: string
  ariaLabel?: string
  required?: boolean
  hintId?: string
}) {
  const [text, setText] = useState(() => formatDisplay(value))
  const [open, setOpen] = useState(false)
  const [bad, setBad] = useState(false)
  const host = useRef<HTMLDivElement | null>(null)

  // Follow the value when it changes from outside -- the apply form seeds both
  // fields, and moves the end when the start passes it.
  useEffect(() => {
    setText(formatDisplay(value))
    setBad(false)
  }, [value])

  const commit = useCallback(
    (raw: string) => {
      const parsed = parseTyped(raw)
      if (parsed) {
        setBad(false)
        onChange(parsed)
      } else if (raw.trim() === '') {
        setBad(false)
        onChange('')
      } else {
        // Left as typed rather than reverted: a captain who mistyped one digit
        // should be able to fix that digit, not retype the date.
        setBad(true)
      }
    },
    [onChange],
  )

  useEffect(() => {
    if (!open) return
    const onDown = (event: MouseEvent) => {
      if (host.current && !host.current.contains(event.target as Node)) setOpen(false)
    }
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  const parts = splitValue(value)

  return (
    <div className="dtf" ref={host}>
      <div className="dtf-row">
        <input
          type="text"
          className={bad ? 'dtf-input dtf-input-bad' : 'dtf-input'}
          value={text}
          onChange={(event) => setText(event.target.value)}
          onBlur={(event) => commit(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault()
              commit(text)
            }
          }}
          placeholder="03 Sep 2026 14:30"
          title={title}
          aria-label={ariaLabel}
          aria-describedby={hintId}
          aria-invalid={bad || undefined}
          required={required}
          inputMode="text"
          autoComplete="off"
          spellCheck={false}
        />
        <button
          type="button"
          className="btn btn-quiet btn-sm dtf-toggle"
          onClick={() => setOpen((was) => !was)}
          aria-expanded={open}
          aria-label={open ? 'Close the calendar' : 'Open the calendar'}
        >
          Calendar
        </button>
      </div>

      {bad && (
        <span className="caption dtf-error">
          Could not read that. Try {ACCEPTED_FORMATS}.
        </span>
      )}

      {open && (
        <Calendar
          parts={parts}
          onPick={(next) => onChange(next)}
          onDone={() => setOpen(false)}
        />
      )}
    </div>
  )
}

function Calendar({
  parts,
  onPick,
  onDone,
}: {
  parts: ReturnType<typeof splitValue>
  onPick: (value: string) => void
  onDone: () => void
}) {
  const now = new Date()
  const selected = parts
  const [view, setView] = useState(() => ({
    year: selected?.year ?? now.getFullYear(),
    month: selected?.month ?? now.getMonth() + 1,
  }))

  const hour = selected?.hour ?? 12
  const minute = selected?.minute ?? 0

  const grid = useMemo(() => {
    const first = new Date(view.year, view.month - 1, 1)
    // getDay() is 0=Sunday; shift so Monday is 0.
    const lead = (first.getDay() + 6) % 7
    const days = new Date(view.year, view.month, 0).getDate()
    const cells: (number | null)[] = Array.from({ length: lead }, () => null)
    for (let day = 1; day <= days; day += 1) cells.push(day)
    while (cells.length % 7 !== 0) cells.push(null)
    return cells
  }, [view])

  const step = (by: number) => {
    setView((current) => {
      const month = current.month + by
      if (month < 1) return { year: current.year - 1, month: 12 }
      if (month > 12) return { year: current.year + 1, month: 1 }
      return { ...current, month }
    })
  }

  const pick = (day: number) => {
    onPick(toValue(view.year, view.month, day, hour, minute))
  }

  const setTime = (nextHour: number, nextMinute: number) => {
    const base = selected ?? {
      year: view.year,
      month: view.month,
      day: now.getDate(),
    }
    onPick(toValue(base.year, base.month, base.day, nextHour, nextMinute))
  }

  const isToday = (day: number) =>
    view.year === now.getFullYear() &&
    view.month === now.getMonth() + 1 &&
    day === now.getDate()

  return (
    <div className="dtf-pop" role="dialog" aria-label="Choose a date and time">
      <div className="dtf-pop-head">
        <button type="button" className="btn btn-quiet btn-sm" onClick={() => step(-1)}
                aria-label="Previous month">
          ‹
        </button>
        <strong>
          {MONTHS_FULL[view.month - 1]} {view.year}
        </strong>
        <button type="button" className="btn btn-quiet btn-sm" onClick={() => step(1)}
                aria-label="Next month">
          ›
        </button>
      </div>

      <div className="dtf-grid dtf-grid-head" aria-hidden="true">
        {WEEKDAYS.map((day) => (
          <span key={day}>{day}</span>
        ))}
      </div>
      <div className="dtf-grid">
        {grid.map((day, index) => (
          <span key={index}>
            {day === null ? (
              <span className="dtf-day dtf-day-empty" />
            ) : (
              <button
                type="button"
                className={[
                  'dtf-day',
                  selected &&
                  selected.year === view.year &&
                  selected.month === view.month &&
                  selected.day === day
                    ? 'dtf-day-on'
                    : '',
                  isToday(day) ? 'dtf-day-today' : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
                onClick={() => pick(day)}
                aria-label={`${day} ${MONTHS_FULL[view.month - 1]} ${view.year}`}
              >
                {day}
              </button>
            )}
          </span>
        ))}
      </div>

      <div className="dtf-time">
        <label>
          <span className="caption">Hour</span>
          <select
            value={hour}
            onChange={(event) => setTime(Number(event.target.value), minute)}
            aria-label="Hour"
          >
            {Array.from({ length: 24 }, (_, h) => (
              <option key={h} value={h}>
                {pad(h)}
              </option>
            ))}
          </select>
        </label>
        <label>
          <span className="caption">Minute</span>
          <select
            value={minute}
            onChange={(event) => setTime(hour, Number(event.target.value))}
            aria-label="Minute"
          >
            {/* Quarter hours only. ABG's choice, 2026-09-03, and it matches
                the form's own default (`nextQuarterHour`): a window is agreed
                with a harbourmaster to the quarter, not to the minute, and
                sixty options is a scroll rather than a choice.

                An off-quarter value already on the row is still offered, so
                opening the calendar on a draft that says 12:07 -- or one an
                agent typed -- cannot silently round it to 12:00. Any minute
                can still be typed into the field. */}
            {QUARTERS.concat(QUARTERS.includes(minute) ? [] : [minute])
              .sort((a, b) => a - b)
              .map((m) => (
                <option key={m} value={m}>
                  {pad(m)}
                </option>
              ))}
          </select>
        </label>
        <button type="button" className="btn btn-sm dtf-done" onClick={onDone}>
          Done
        </button>
      </div>
    </div>
  )
}
